/**
 * The menu behind an excerpt's icon, in its source or in a destination (#277):
 * open in context, open the task/note it made, change destinations, adjust the
 * range (source only), remove. Where several excerpts start at one point the
 * menu first asks which.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, ListTree, Move, Trash2, Square, SquareCheckBig, StickyNote } from 'lucide-react';
import { apiPatch } from '../../lib/api';
import { useTaskStatus } from '../../lib/taskStatusCache';
import { isTaskClosed } from '../taskIcon';
import { ExcerptKindIcon } from './ExcerptKindIcon';
import { useExcerptStore, useExcerptStoreVersion } from '../../contexts/ExcerptSyncContext';
import { useToast } from '../../contexts/ToastContext';
import type { ExcerptView } from '../../lib/excerpts';
import { DestinationPicker } from './DestinationPicker';
import { DismissBackdrop } from '../DismissBackdrop';
import { changeDestinations, itemUrl, removeExcerpt, removeFrom, sourceUrl } from './excerptActions';

export interface ExcerptMenuProps {
  ids: string[];
  anchor: HTMLElement;
  /** Where the menu was opened: the source document, or a destination holding an embed. */
  where: 'source' | 'destination';
  /** The document the menu was opened in. */
  host?: { type: string; id: string };
  onAdjust?: (id: string) => void;
  onClose: () => void;
}

const snippet = (v: ExcerptView) => {
  const t = (v.plain_text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > 60 ? `${t.slice(0, 57)}...` : t || 'Excerpt';
};

export function ExcerptMenu({ ids, anchor, where, host, onAdjust, onClose }: ExcerptMenuProps) {
  const store = useExcerptStore();
  useExcerptStoreVersion(store);
  const navigate = useNavigate();
  const toast = useToast();
  const [chosen, setChosen] = useState<string | null>(ids.length === 1 ? ids[0] : null);
  const [picking, setPicking] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const width = 240;
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!picking && ref.current && !ref.current.contains(e.target as Node) && e.target !== anchor) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !picking) onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [anchor, onClose, picking]);

  const v = chosen ? store?.view(chosen) : null;
  const taskId = v?.kind === 'task' ? v.linked_entity_id : null;
  const taskStatus = useTaskStatus(taskId);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(label);
    } catch {
      toast.error('That did not work - please try again');
    }
    onClose();
  };

  if (picking && v) {
    return (
      <DestinationPicker
        title="Change destinations"
        confirmLabel="Save"
        initial={v.destinations.map((d) => ({ type: d.type as never, id: d.id, title: d.title }))}
        exclude={{ type: v.source.type as never, id: v.source.id }}
        onClose={() => { setPicking(false); onClose(); }}
        onConfirm={(picked) => { setPicking(false); void run('Destinations updated', () => changeDestinations(store, v, picked)); }}
      />
    );
  }

  const item = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-hover';
  return createPortal(
    <>
    {/* #121: a tap outside only closes the menu - it does not also land on what is underneath. */}
    <DismissBackdrop zClass="z-[64]" onDismiss={onClose} />
    <div ref={ref} role="menu" aria-label="Excerpt" className="fixed z-[65] w-60 rounded-md border border-border bg-bg-surface py-1 shadow-xl" style={pos}>
      {!chosen && ids.map((id) => {
        const cv = store?.view(id);
        return (
          <button key={id} type="button" role="menuitem" className={item} onClick={() => setChosen(id)}>
            <ExcerptKindIcon kind={cv?.kind ?? 'excerpt'} linkedId={cv?.linked_entity_id ?? null} size={13} className="shrink-0 text-excerpt" />
            <span className="truncate">{cv ? snippet(cv) : 'Loading...'}</span>
          </button>
        );
      })}
      {chosen && !v && <div className="px-3 py-2 text-xs text-text-muted">Loading...</div>}
      {v && !confirmRemove && (
        <>
          <div className="px-3 pb-1 pt-0.5 text-[11px] text-text-muted truncate">{snippet(v)}</div>
          {where === 'destination' && (
            <button type="button" role="menuitem" className={item} onClick={() => { onClose(); navigate(sourceUrl(v)); }}>
              <ExternalLink size={13} /> Open in context
            </button>
          )}
          {v.linked_entity_id && (
            <button type="button" role="menuitem" className={item} onClick={() => { onClose(); navigate(itemUrl(v.kind, v.linked_entity_id!)); }}>
              {v.kind === 'task' ? <ExcerptKindIcon kind="task" linkedId={v.linked_entity_id} size={13} /> : <StickyNote size={13} />} Open {v.kind}
            </button>
          )}
          {taskId && taskStatus && (isTaskClosed(taskStatus) ? (
            <button type="button" role="menuitem" className={item}
              onClick={() => void run('Task marked incomplete', () => apiPatch(`/tasks/${encodeURIComponent(taskId)}`, { status: 'todo' }))}>
              <Square size={13} /> Mark incomplete
            </button>
          ) : (
            <button type="button" role="menuitem" className={item}
              onClick={() => void run('Task marked complete', () => apiPatch(`/tasks/${encodeURIComponent(taskId)}`, { status: 'done' }))}>
              <SquareCheckBig size={13} /> Mark complete
            </button>
          ))}
          <button type="button" role="menuitem" className={item} onClick={() => setPicking(true)}>
            <ListTree size={13} /> Change destinations
          </button>
          {where === 'source' && onAdjust && (
            <button type="button" role="menuitem" className={item} onClick={() => { onAdjust(v.id); onClose(); }}>
              <Move size={13} /> Adjust range
            </button>
          )}
          {where === 'destination' && host ? (
            <button type="button" role="menuitem" className={item} onClick={() => void run('Removed from this item', () => removeFrom(store, v, host))}>
              <Trash2 size={13} /> Remove from here
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => (v.linked_entity_id ? setConfirmRemove(true) : void run('Excerpt removed', () => removeExcerpt(store, v, false)))}
            >
              <Trash2 size={13} /> Remove excerpt
            </button>
          )}
        </>
      )}
      {v && confirmRemove && (
        <div className="px-3 py-2 space-y-1.5">
          <div className="text-xs text-text-secondary">Remove this {v.kind} excerpt?</div>
          <button type="button" className={`${item} rounded`} onClick={() => void run('Highlight removed', () => removeExcerpt(store, v, false))}>
            Remove highlight only
          </button>
          <button type="button" className={`${item} rounded text-danger`} onClick={() => void run(`Excerpt and ${v.kind} removed`, () => removeExcerpt(store, v, true))}>
            Also delete the {v.kind}
          </button>
        </div>
      )}
    </div>
    </>,
    document.body,
  );
}
